import { css } from "@emotion/react";

export function alertsPanelCSS() {
    return css`
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: rgba(15, 23, 42, 0.45);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
        animation: ap-fade-in 180ms ease-out;
        font-family: 'Neue Haas Grotesk Display Pro', -apple-system, BlinkMacSystemFont,
            'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

        @keyframes ap-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
        }

        @keyframes ap-pop-in {
            from { opacity: 0; transform: translateY(8px) scale(0.98); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes ap-shimmer {
            0%   { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }

        .ap-container {
            background: #fefefe;
            border-radius: 0.8rem;
            width: 100%;
            max-width: 1000px;
            max-height: 88vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
            animation: ap-pop-in 220ms ease-out;
        }

        @media (max-width: 650px) {
            padding: 12px;
            .ap-container { max-height: 94vh; }
        }

        /* ── Header ── */
        .ap-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 20px;
            border-bottom: 1px solid #e2e8f0;
            background: #f8fafc;
        }
        .ap-title-block {
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .ap-title-icon {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            background: linear-gradient(135deg, #34C6F1, #00BA6E);
            color: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.95rem;
        }
        .ap-title {
            margin: 0;
            font-size: 1.05rem;
            font-weight: 700;
            color: #0f172a;
            letter-spacing: 0.02em;
        }
        .ap-header-actions {
            display: flex;
            gap: 4px;
            align-items: center;
        }
        .ap-icon-btn {
            background: transparent;
            border: none;
            cursor: pointer;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            color: #64748b;
            font-size: 0.95rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: background 160ms ease, color 160ms ease;
        }
        .ap-icon-btn:hover { background: #f1f5f9; color: #0f172a; }
        .ap-icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .ap-icon-btn.refresh { color: #1A9AC4; }
        .ap-icon-btn.refresh:hover { background: #e0f2fe; }

        /* ── Filters ── */
        .ap-filters {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 20px;
            border-bottom: 1px solid #e2e8f0;
            background: #fff;
            flex-wrap: wrap;
        }
        .ap-status-pills {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
        }
        .ap-pill {
            background: #fff;
            border: 1px solid #e2e8f0;
            color: #475569;
            padding: 5px 12px;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 160ms ease;
            letter-spacing: 0.02em;
        }
        .ap-pill:hover { border-color: #cbd5e1; background: #f8fafc; }
        .ap-pill.active {
            background: #00BA6E;
            border-color: #00BA6E;
            color: #fff;
        }
        .ap-search-box {
            position: relative;
            flex: 1;
            min-width: 200px;
            display: flex;
            align-items: center;
        }
        .ap-search-box .pi {
            position: absolute;
            left: 12px;
            color: #94a3b8;
            font-size: 0.75rem;
            pointer-events: none;
        }
        .ap-search-input {
            width: 100%;
            border: 1.5px solid #34C6F1;
            border-radius: 16px;
            padding: 6px 12px 6px 30px;
            font-size: 0.8rem;
            font-weight: 500;
            color: #0f172a;
            outline: none;
            font-family: inherit;
            transition: box-shadow 160ms ease;
        }
        .ap-search-input::placeholder { color: #A7A9AC; }
        .ap-search-input:focus {
            box-shadow: 0 0 0 2px rgba(52, 198, 241, 0.25);
        }

        @media (max-width: 650px) {
            .ap-filters { flex-direction: column; align-items: stretch; }
            .ap-search-box { min-width: 0; }
        }

        /* ── List ── */
        .ap-list {
            overflow-y: auto;
            flex: 1;
            background: #fafbfc;
        }
        .ap-list::-webkit-scrollbar { width: 8px; }
        .ap-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .ap-list::-webkit-scrollbar-track { background: transparent; }

        /* ── Card ── */
        .ap-card {
            position: relative;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px 10px 14px;
            background: #fff;
            border-bottom: 1px solid #eef2f6;
            border-left: 4px solid var(--sev-border, #34C6F1);
            cursor: pointer;
            transition: background 160ms ease;
            min-height: 72px;
        }
        .ap-card:hover { background: #f8fafc; }
        .ap-card.unseen { background: #fbfeff; }
        .ap-card.unseen::before {
            content: '';
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #34C6F1;
            box-shadow: 0 0 0 3px rgba(52, 198, 241, 0.18);
        }

        .ap-card-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            background: var(--sev-bg, #e0f2fe);
            color: var(--sev-fg, #075985);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 1rem;
            flex-shrink: 0;
        }

        .ap-card-body { flex: 1; min-width: 0; }
        .ap-card-title-row {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .ap-card-title {
            font-size: 0.88rem;
            font-weight: 600;
            color: #0f172a;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .ap-card-message {
            font-size: 0.78rem;
            color: #64748b;
            margin-top: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .ap-card-meta {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-top: 4px;
            font-size: 0.68rem;
            color: #94a3b8;
            letter-spacing: 0.02em;
        }
        .ap-card-meta .sep {
            width: 3px;
            height: 3px;
            border-radius: 50%;
            background: #cbd5e1;
            display: inline-block;
        }

        .ap-sev-badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 999px;
            font-size: 0.62rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            background: var(--sev-bg);
            color: var(--sev-fg);
            border: 1px solid var(--sev-border);
            flex-shrink: 0;
        }
        .ap-status-chip {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 1px 7px;
            border-radius: 999px;
            font-size: 0.62rem;
            font-weight: 600;
            background: #f1f5f9;
            color: #475569;
            text-transform: uppercase;
            letter-spacing: 0.04em;
        }
        .ap-status-chip.silenced { background: #fef3c7; color: #92400e; }
        .ap-status-chip.ignored  { background: #e2e8f0; color: #475569; }
        .ap-status-chip.resolved { background: #dcfce7; color: #166534; }
        .ap-status-chip.open     { background: #e0f2fe; color: #075985; }

        /* ── Actions ── */
        .ap-card-actions {
            display: flex;
            gap: 4px;
            align-items: center;
            position: relative;
            flex-shrink: 0;
        }
        .ap-action-btn {
            background: transparent;
            border: none;
            border-radius: 8px;
            width: 30px;
            height: 30px;
            cursor: pointer;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: #64748b;
            transition: all 160ms ease;
        }
        .ap-action-btn:hover { background: #f1f5f9; }
        .ap-action-btn.ignore:hover  { background: #f1f5f9; color: #475569; }
        .ap-action-btn.silence:hover { background: #fef3c7; color: #92400e; }
        .ap-action-btn.resolve:hover { background: #dcfce7; color: #166534; }
        .ap-action-btn:disabled { cursor: progress; opacity: 0.75; }
        .ap-card.busy { pointer-events: none; opacity: 0.85; }
        .ap-card.busy .ap-card-actions { pointer-events: auto; }

        .ap-silence-pop {
            position: absolute;
            right: 0;
            top: calc(100% + 6px);
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
            padding: 10px;
            z-index: 5;
            min-width: 200px;
            animation: ap-pop-in 160ms ease-out;
        }
        .ap-silence-label {
            font-size: 0.65rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #64748b;
            margin: 0 0 6px;
        }
        .ap-silence-chips {
            display: flex;
            gap: 4px;
            margin-bottom: 8px;
        }
        .ap-silence-chip {
            flex: 1;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 4px 8px;
            font-size: 0.7rem;
            font-weight: 600;
            color: #475569;
            cursor: pointer;
            transition: all 140ms ease;
        }
        .ap-silence-chip:hover {
            background: #fef3c7;
            border-color: #f59e0b;
            color: #92400e;
        }
        .ap-silence-custom {
            display: flex;
            gap: 4px;
        }
        .ap-silence-custom input {
            flex: 1;
            border: 1.5px solid #34C6F1;
            border-radius: 8px;
            padding: 4px 8px;
            font-size: 0.75rem;
            font-family: inherit;
            outline: none;
            min-width: 0;
        }
        .ap-silence-custom input:focus {
            box-shadow: 0 0 0 2px rgba(52, 198, 241, 0.25);
        }
        .ap-silence-custom button {
            background: #E2A03F;
            color: #fff;
            border: none;
            border-radius: 8px;
            padding: 4px 10px;
            font-size: 0.7rem;
            font-weight: 700;
            cursor: pointer;
            transition: background 140ms ease;
        }
        .ap-silence-custom button:hover { background: #c98a30; }

        /* ── Empty ── */
        .ap-empty {
            padding: 48px 32px;
            text-align: center;
            color: #64748b;
        }
        .ap-empty-icon {
            font-size: 2.4rem;
            color: #00BA6E;
            margin-bottom: 8px;
        }
        .ap-empty-title {
            font-size: 0.95rem;
            font-weight: 700;
            color: #0f172a;
            margin: 0 0 4px;
        }
        .ap-empty-sub {
            font-size: 0.8rem;
            color: #94a3b8;
            margin: 0;
        }

        /* ── Skeletons ── */
        .ap-skeleton {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            border-bottom: 1px solid #eef2f6;
            background: #fff;
            min-height: 72px;
            border-left: 4px solid #e2e8f0;
        }
        .ap-skeleton-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
            background-size: 800px 100%;
            animation: ap-shimmer 1.4s linear infinite;
            flex-shrink: 0;
        }
        .ap-skeleton-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }
        .ap-skeleton-line {
            height: 10px;
            border-radius: 4px;
            background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
            background-size: 800px 100%;
            animation: ap-shimmer 1.4s linear infinite;
        }
        .ap-skeleton-line.short { width: 30%; }
        .ap-skeleton-line.mid   { width: 60%; }
        .ap-skeleton-line.long  { width: 90%; }

        /* ── Footer ── */
        .ap-footer {
            padding: 10px 20px;
            border-top: 1px solid #e2e8f0;
            background: #f8fafc;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            font-size: 0.72rem;
            color: #64748b;
            letter-spacing: 0.02em;
        }
        .ap-footer .count { font-weight: 700; color: #0f172a; }
    `;
}
